import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, IconButton, TextField, Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from '../../../../lib/axios';
import { toast, ToastContainer } from 'react-toastify';

const IngredientsBOM = ({ rows, setRows, mode }) => {

    const [items, setItems] = useState([]);
    const inputRefs = useRef([]);
    const [focusNextRow, setFocusNextRow] = useState(false);

    useEffect(() => {
        const fetchIngredients = async () => {
            try {
                const response = await axiosInstance.post('MstItm/GetMstItmdrp', {
                    ItmSubGrpI: 1,
                    Flag: "Food",
                });

                if (response.data.Status === 0 && response.data.Data) {
                    setItems(response.data.Data);
                } else {
                    toast.info("No ingredients found");
                }
            } catch (error) {
                toast.error("Failed to load ingredients");
            }
        };

        fetchIngredients();
    }, []);

    useEffect(() => {
        const lastRow = rows[rows.length - 1];
        if (lastRow?.ingredient) {
            const shouldAdd = rows.every((r, idx) => idx === rows.length - 1 || r.ingredient !== null);
            if (shouldAdd) {
                const newId = Date.now();
                setRows((prev) => [
                    ...prev,
                    {
                        id: newId,
                        ingredient: null,
                        qty: '0.00',
                        unit: '',
                        extraQty: '0.00',
                    },
                ]);
                setFocusNextRow(true);
            }
        }
    }, [rows]);

    useEffect(() => {
        if (focusNextRow) {
            const lastIndex = rows.length - 1;
            if (inputRefs.current[lastIndex]) {
                const input = inputRefs.current[lastIndex].querySelector('input');
                if (input) input.focus();
            }
            setFocusNextRow(false);
        }
    }, [rows, focusNextRow]);

    const isDuplicate = (selectedItem, currentRowId) => {
        if (!selectedItem) return false;
        const name = selectedItem.ItmName || selectedItem.Name || '';
        return rows.some(
            (r) => r.id !== currentRowId && (r.ingredient?.ItmName || r.ingredient?.Name || '') === name
        );
    };

    const handleIngredientChange = (rowId, newValue) => {
        if (newValue) {
            if (isDuplicate(newValue, rowId)) {
                toast.warn("This ingredient is already added!");
                return;
            }

            updateRow(rowId, 'ingredient', newValue);
            updateRow(rowId, 'qty', '0.00');
            updateRow(rowId, 'unit', newValue.UnitName || '');
            updateRow(rowId, 'extraQty', '0.00');
        } else {
            updateRow(rowId, 'ingredient', null);
            updateRow(rowId, 'qty', '0.00');
            updateRow(rowId, 'unit', '');
            updateRow(rowId, 'extraQty', '0.00');
        }
    };

    const updateRow = (id, field, value) => {
        setRows((prev) =>
            prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
        );
    };

    const deleteRow = (id) => {
        if (rows.length === 1) return;
        setRows((prev) =>
            prev.map((row) =>
                row.id === id ? { ...row, isDeleted: true } : row
            )
        );
    };

    return (
        <>
            <ToastContainer position="top-right" autoClose={2200} />
            <Box sx={{ mt: 0 }}>
                <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                        maxHeight: 350,
                        overflow: 'auto',
                        borderRadius: 1,
                        '& .MuiTableCell-root': {
                            fontSize: '0.81rem',
                            py: '2px',
                            px: '8px',
                        },
                    }}
                >
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'medium', width: '5%' }}>
                                    Sr.No
                                </TableCell>
                                <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'medium', width: '40%' }}>
                                    Ingredients
                                </TableCell>
                                <TableCell align="center" sx={{ bgcolor: 'grey.100', fontWeight: 'medium', width: '12%' }}>
                                    Qty
                                </TableCell>
                                <TableCell align="center" sx={{ bgcolor: 'grey.100', fontWeight: 'medium', width: '12%' }}>
                                    Extra Qty
                                </TableCell>
                                <TableCell align="center" sx={{ bgcolor: 'grey.100', fontWeight: 'medium', width: '12%' }}>
                                    Unit
                                </TableCell>
                                <TableCell align="center" sx={{ bgcolor: 'grey.100', fontWeight: 'medium', width: '7%' }}>
                                    Delete
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {rows
                                .filter((row) => !row.isDeleted)
                                .map((row, index) => (
                                    <TableRow
                                        key={row.id}
                                        hover
                                        sx={{
                                            height: 32,
                                            '& td': { borderBottom: '1px solid #eee' },
                                        }}
                                    >
                                        <TableCell sx={{ py: '1px' }}>{index + 1}</TableCell>

                                        <TableCell sx={{ py: '1px' }}>
                                            <Autocomplete
                                                freeSolo
                                                size="small"
                                                options={items}
                                                getOptionLabel={(option) => option ? (option.ItmName || option.Name || 'Unnamed') : ''}
                                                value={row.ingredient || null}
                                                onChange={(_, newValue) => handleIngredientChange(row.id, newValue)}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder="Search ingredient..."
                                                        variant="outlined"
                                                        size="small"
                                                        inputRef={(el) => (inputRefs.current[index] = el?.parentElement)}
                                                        sx={{
                                                            '& .MuiInputBase-input': {
                                                                fontSize: '0.81rem',
                                                                py: '2px',
                                                            },
                                                            '& .MuiOutlinedInput-root': {
                                                                height: 26,
                                                                borderRadius: '4px',
                                                            },
                                                        }}
                                                    />
                                                )}
                                                renderOption={(props, option) => (
                                                    <li {...props} style={{ fontSize: '0.81rem' }}>
                                                        {option.ItmName || option.Name || 'NA'}
                                                    </li>
                                                )}
                                                filterOptions={(options, { inputValue }) =>
                                                    options.filter((opt) =>
                                                        (opt.ItmName || opt.Name || '')
                                                            .toLowerCase()
                                                            .includes(inputValue.toLowerCase())
                                                    )
                                                }
                                                sx={{ width: '100%' }}
                                                disabled={mode === 'view'}
                                            />
                                        </TableCell>

                                        <TableCell align="center" sx={{ py: '1px' }}>
                                            <TextField
                                                size="small"
                                                type="number"
                                                inputProps={{ min: 0, step: 0.001 }}
                                                value={row.qty}
                                                disabled={true}
                                                onChange={(e) => updateRow(row.id, 'qty', e.target.value)}
                                                sx={{
                                                    width: 68,
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '0.81rem',
                                                        py: '2px',
                                                        textAlign: 'center',
                                                    },
                                                }}
                                            />
                                        </TableCell>

                                        <TableCell align="center" sx={{ py: '1px' }}>
                                            <TextField
                                                size="small"
                                                type="number"
                                                inputProps={{ min: 0, step: 0.001 }}
                                                value={row.extraQty}
                                                disabled={true}
                                                onChange={(e) => updateRow(row.id, 'extraQty', e.target.value)}
                                                sx={{
                                                    width: 68,
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '0.81rem',
                                                        py: '2px',
                                                        textAlign: 'center',
                                                    },
                                                }}
                                            />
                                        </TableCell>

                                        <TableCell align="center" sx={{ py: '1px' }}>
                                            <TextField
                                                size="small"
                                                value={row.unit}
                                                onChange={(e) => updateRow(row.id, 'unit', e.target.value)}
                                                disabled={true}
                                                sx={{
                                                    width: 68,
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '0.81rem',
                                                        py: '2px',
                                                        textAlign: 'center',
                                                    },
                                                }}
                                            />
                                        </TableCell>

                                        <TableCell align="center" sx={{ py: '1px' }}>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => deleteRow(row.id)}
                                                disabled={mode === 'view'}
                                                sx={{ p: 0.4 }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}

                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.disabled', fontSize: '0.88rem' }}>
                                        No items added yet
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </>
    );
};

export default IngredientsBOM;